import { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Crown, X, Sparkles, Copy } from "lucide-react";
import { toast } from "sonner";
import { copyText } from "../lib/clipboard";
import { api } from "../lib/api";
import { useRoomSync } from "../lib/useRoomSync";

const AVATAR_MAP = {
  "pink-w":   { img: "/assets/home/avatar-pink-w.png",   ring: "#FF2E9A" },
  "orange-w": { img: "/assets/home/avatar-orange-w.png", ring: "#FFE082" },
  "purple-w": { img: "/assets/home/avatar-purple-w.png", ring: "#9B6BFF" },
  "red-m":    { img: "/assets/home/avatar-red-m.png",    ring: "#FF8A00" },
  "blue-m":   { img: "/assets/home/avatar-blue-m.png",   ring: "#3DA4FF" },
  "green-m":  { img: "/assets/home/avatar-green-m.png",  ring: "#3DD68C" },
};
const DEFAULT_RING = "#FFE082";

function avatarSrc(id) { return AVATAR_MAP[id]?.img; }
function avatarRing(id) { return AVATAR_MAP[id]?.ring || DEFAULT_RING; }

/**
 * Screen 3 — Squad Lobby.
 * Polls /api/group-rooms/{code} every 2s. Shows live roster: cinematic avatar + name + role badge.
 * Host sees Kick affordance per player. Big "Start Game →" CTA enabled when ≥ 3 players.
 */
export default function PlayGroupSquad({ code, isHost, onBack, onPickPack, onGameStarted }) {
  const [loading, setLoading] = useState(false);

  // SSE-backed sync — auto-advances guest when host starts.
  const { data: room } = useRoomSync({
    code,
    endpoint: "group-rooms",
    fetchFn: useCallback(() => api.getGroupRoom(code), [code]),
  });

  // Guest auto-advance when host starts a round
  useEffect(() => {
    if (!room) return;
    if (!isHost && room.status === "playing" && onGameStarted) {
      onGameStarted(room);
    }
  }, [room, isHost, onGameStarted]);

  if (!room) {
    return (
      <div className="vc-bg min-h-screen flex items-center justify-center text-white/70" data-testid="group-squad-loading">
        Loading squad…
      </div>
    );
  }

  const players = room.players || [];
  const playerCount = players.length;
  const canStart = isHost && playerCount >= (room.min_players || 3);

  const handleKick = async (playerId) => {
    if (!isHost) return;
    try {
      await api.kickGroupPlayer(code, playerId);
      toast.success("Player removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't remove player");
    }
  };

  const copyCode = async () => {
    const ok = await copyText(code);
    if (ok) toast.success("Code copied 💌");
  };

  const handleStart = () => {
    if (!canStart) {
      toast.error(`Need at least ${room.min_players || 3} players`);
      return;
    }
    onPickPack?.(room);
  };

  return (
    <div className="vc-bg min-h-screen relative overflow-hidden" data-testid="group-squad-screen">
      {/* Bloom */}
      <div
        className="absolute pointer-events-none rounded-full"
        style={{
          top: 40, left: "50%", transform: "translateX(-50%)",
          width: 460, height: 460,
          background: "radial-gradient(circle, rgba(255, 138, 0, 0.32) 0%, rgba(255, 184, 0, 0.14) 38%, transparent 70%)",
          filter: "blur(50px)", zIndex: 0,
        }}
        aria-hidden
      />

      <main className="relative z-[5] max-w-md mx-auto px-5 pt-5 pb-12 min-h-screen flex flex-col">
        {/* Top bar */}
        <div className="flex items-center justify-between mb-3">
          <button
            onClick={onBack}
            aria-label="Back"
            data-testid="group-squad-back"
            className="w-10 h-10 rounded-full flex items-center justify-center text-white/85 hover:text-white transition active:scale-95"
            style={{ background: "rgba(255, 255, 255, 0.06)", border: "1px solid rgba(255, 255, 255, 0.10)", backdropFilter: "blur(8px)" }}
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <button
            onClick={copyCode}
            data-testid="group-squad-code-pill"
            className="px-4 py-1.5 rounded-full flex items-center gap-2 transition active:scale-95"
            style={{
              background: "rgba(15, 8, 22, 0.65)",
              border: "1.5px solid #FF8A00",
              boxShadow: "0 0 12px rgba(255, 138, 0, 0.5)",
            }}
          >
            <span className="text-white text-[13px] font-extrabold" style={{ letterSpacing: "0.08em", fontFamily: "'Manrope', sans-serif" }}>
              {code}
            </span>
            <Copy className="w-3.5 h-3.5" style={{ color: "#FFE082" }} strokeWidth={2.4} />
          </button>
          <div className="w-10" aria-hidden />
        </div>

        {/* Title */}
        <div className="text-center mb-2 mt-2">
          <h1
            className="font-extrabold leading-[0.95] tracking-tight mb-2"
            style={{
              fontFamily: "'Manrope', sans-serif",
              fontSize: "clamp(2.0rem, 8.5vw, 2.6rem)",
              letterSpacing: "-0.03em",
              background: "linear-gradient(90deg, #FFF3C2 0%, #FFB800 55%, #FF8A00 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              textShadow: "0 0 1px rgba(255, 184, 0, 0.6)",
              filter: "drop-shadow(0 1px 1.5px rgba(0, 0, 0, 0.55))",
            }}
            data-testid="group-squad-title"
          >
            The Squad 🎉
          </h1>
          <p className="text-white/75 text-[14px] font-medium" data-testid="group-squad-count">
            <span className="text-white font-extrabold">{playerCount}</span>
            <span className="text-white/55"> / {room.max_players || 20} joined</span>
            {playerCount < (room.min_players || 3) && (
              <> · Need <span className="text-[#FFE082] font-extrabold">{(room.min_players || 3) - playerCount}</span> more</>
            )}
          </p>
        </div>

        {/* Roster */}
        <div className="flex-1 mt-3 space-y-2.5 overflow-y-auto pr-1" data-testid="group-squad-roster" style={{ maxHeight: "60vh" }}>
          <AnimatePresence initial={false}>
            {players.map((p) => {
              const isHostPlayer = p.role === "host";
              const ring = avatarRing(p.avatar_id);
              return (
                <motion.div
                  key={p.id}
                  initial={{ opacity: 0, y: 10, scale: 0.94 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, x: -20, scale: 0.94 }}
                  transition={{ type: "spring", stiffness: 320, damping: 24 }}
                  className="flex items-center gap-3 rounded-2xl px-3 py-2.5"
                  style={{
                    background: "rgba(20, 10, 14, 0.55)",
                    border: `1.5px solid ${ring}55`,
                    boxShadow: `0 0 10px ${ring}30`,
                  }}
                  data-testid={`group-squad-player-${p.id}`}
                >
                  <div
                    className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0"
                    style={{
                      border: `2.5px solid ${ring}`,
                      boxShadow: `0 0 12px ${ring}88, inset 0 0 6px ${ring}22`,
                    }}
                  >
                    {avatarSrc(p.avatar_id) ? (
                      <img src={avatarSrc(p.avatar_id)} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-white font-extrabold" style={{ background: `${ring}33` }}>
                        {(p.name || "?")[0].toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-white text-[15px] font-extrabold truncate">
                        {p.name || "Guest"}
                      </span>
                      {isHostPlayer && (
                        <Crown
                          className="w-3.5 h-3.5 flex-shrink-0"
                          style={{ color: "#FFE082", filter: "drop-shadow(0 0 4px #FFE082)" }}
                          strokeWidth={2.4}
                          fill="#FFE082"
                        />
                      )}
                    </div>
                    <div className="text-[#3DD68C] text-[11px] font-bold flex items-center gap-1 mt-0.5">
                      <span
                        className="w-1.5 h-1.5 rounded-full"
                        style={{ background: "#3DD68C", boxShadow: "0 0 6px #3DD68C" }}
                      />
                      {isHostPlayer ? "Host" : "Joined"}
                    </div>
                  </div>
                  {isHost && !isHostPlayer && (
                    <button
                      onClick={() => handleKick(p.id)}
                      data-testid={`group-squad-kick-${p.id}`}
                      aria-label={`Remove ${p.name}`}
                      className="w-8 h-8 rounded-full flex items-center justify-center text-white/55 hover:text-white transition active:scale-95"
                      style={{ background: "rgba(255, 184, 0, 0.10)", border: "1px solid rgba(255, 184, 0, 0.4)" }}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </motion.div>
              );
            })}
          </AnimatePresence>

          {/* "Waiting for more…" hint */}
          {playerCount < (room.min_players || 3) && (
            <div
              className="rounded-2xl px-4 py-3 text-center mt-2"
              style={{
                background: "rgba(20, 10, 14, 0.4)",
                border: "1.5px dashed rgba(255, 180, 71, 0.4)",
              }}
            >
              <motion.span
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
                className="inline-block text-[18px] mr-1.5"
              >
                ✨
              </motion.span>
              <span className="text-white/75 text-[12.5px] font-bold">
                Waiting for more friends to join…
              </span>
            </div>
          )}
        </div>

        {/* Start CTA — host only */}
        {isHost && (
          <button
            onClick={handleStart}
            disabled={!canStart || loading}
            data-testid="group-squad-start"
            className="vc-cta vc-cta-primary w-full rounded-full py-[18px] font-extrabold flex items-center justify-center gap-2 mt-4"
            style={{ fontSize: "17px" }}
          >
            Start Game
            <ArrowRight className="w-5 h-5" strokeWidth={3} />
          </button>
        )}

        {/* Guest waiting state */}
        {!isHost && (
          <div
            className="rounded-2xl mt-4 py-3.5 px-4 text-center"
            style={{
              background: "rgba(20, 10, 14, 0.55)",
              border: "1.5px solid rgba(255, 138, 0, 0.45)",
              boxShadow: "0 0 12px rgba(255, 138, 0, 0.3)",
            }}
            data-testid="group-squad-guest-waiting"
          >
            <Sparkles className="w-4 h-4 inline mr-1.5" style={{ color: "#FFE082" }} />
            <span className="text-white text-[13.5px] font-bold">
              {players.find((p) => p.role === "host")?.name || "Host"} will start the game when ready
            </span>
          </div>
        )}
      </main>
    </div>
  );
}
